import React from "react";
import { Link } from "react-router-dom";
import { Loader2, LogIn, Ticket, ShoppingBag, MapPin, CalendarDays } from "lucide-react";
import api from "../lib/api";
import { useMember } from "../context/MemberAuthContext";
import { useSEO, Reveal } from "../components/site/Reveal";

const STATUS_STYLES = {
  paid: "bg-forest/10 text-forest",
  confirmed: "bg-forest/10 text-forest",
  not_required: "bg-forest/10 text-forest",
  pending: "bg-gold/15 text-gold-dark",
  completed: "bg-forest/10 text-forest",
};

function Badge({ status }) {
  const label = { paid: "Paid", confirmed: "Confirmed", not_required: "Registered", pending: "Deposit due", completed: "Paid" }[status] || status;
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[status] || "bg-parchment text-ink-muted"}`}>{label}</span>;
}

export default function Account() {
  useSEO("My Account", "Your ÀsÀNigHT Worldwide trip registrations and orders.");
  const { member, loading, signIn, signOut } = useMember();
  const [regs, setRegs] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [busy, setBusy] = React.useState(true);

  React.useEffect(() => {
    if (!member) { setBusy(false); return; }
    setBusy(true);
    Promise.all([
      api.get("/member/registrations").then((r) => setRegs(r.data)).catch(() => setRegs([])),
      api.get("/member/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])),
    ]).finally(() => setBusy(false));
  }, [member]);

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div>;
  }

  if (!member) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6 py-24">
        <div className="max-w-md w-full text-center bg-cream border border-parchment rounded-2xl p-10" data-testid="account-signin-prompt">
          <div className="w-14 h-14 rounded-full bg-forest/10 grid place-items-center mx-auto mb-5"><LogIn className="w-7 h-7 text-forest" /></div>
          <h1 className="font-serif text-3xl text-ink mb-2">Sign in to your account</h1>
          <p className="text-ink-muted mb-6">Sign in with Google to see your trip registrations and orders, and to check out faster.</p>
          <button onClick={signIn} data-testid="account-signin-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-6 py-3 rounded-full hover:bg-forest-light transition-colors">
            <LogIn className="w-4 h-4" /> Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-14" data-testid="account-page">
      <Reveal>
        <div className="flex items-center gap-4 mb-10">
          {member.picture
            ? <img src={member.picture} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-gold/40" referrerPolicy="no-referrer" />
            : <span className="grid place-items-center w-16 h-16 rounded-full bg-forest text-cream text-2xl font-serif">{(member.name || member.email || "?").charAt(0).toUpperCase()}</span>}
          <div>
            <h1 className="font-serif text-3xl text-ink">{member.name || "Welcome"}</h1>
            <p className="text-ink-muted text-sm">{member.email}</p>
          </div>
          <button onClick={signOut} data-testid="account-signout-button" className="ml-auto text-sm text-ink-muted hover:text-forest underline">Sign out</button>
        </div>
      </Reveal>

      {busy ? (
        <div className="grid place-items-center py-16"><Loader2 className="w-7 h-7 animate-spin text-forest" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-10">
          {/* Registrations */}
          <section data-testid="account-registrations">
            <h2 className="flex items-center gap-2 font-serif text-2xl text-ink mb-4"><Ticket className="w-5 h-5 text-gold" /> My Trip Registrations</h2>
            {regs.length === 0 ? (
              <p className="text-ink-muted text-sm bg-cream border border-parchment rounded-xl p-5">No registrations yet. <Link to="/events" className="text-forest underline">Browse trips →</Link></p>
            ) : (
              <div className="space-y-3">
                {regs.map((r) => (
                  <div key={r.id} className="bg-cream border border-parchment rounded-xl p-4" data-testid={`account-reg-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold text-ink">{r.event?.title || r.event_title || "Trip"}</h3>
                      <Badge status={r.payment_status} />
                    </div>
                    {r.event?.date && <p className="flex items-center gap-1.5 text-xs text-ink-muted mt-1"><CalendarDays className="w-3.5 h-3.5" /> {r.event.date}</p>}
                    {r.event?.location && <p className="flex items-center gap-1.5 text-xs text-ink-muted mt-0.5"><MapPin className="w-3.5 h-3.5" /> {r.event.location}</p>}
                    {r.headcount > 1 && <p className="text-xs text-ink-muted mt-1">Group of {r.headcount}</p>}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Orders */}
          <section data-testid="account-orders">
            <h2 className="flex items-center gap-2 font-serif text-2xl text-ink mb-4"><ShoppingBag className="w-5 h-5 text-gold" /> My Orders</h2>
            {orders.length === 0 ? (
              <p className="text-ink-muted text-sm bg-cream border border-parchment rounded-xl p-5">No orders yet. <Link to="/shop" className="text-forest underline">Visit the shop →</Link></p>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <div key={o.id} className="bg-cream border border-parchment rounded-xl p-4" data-testid={`account-order-${o.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold text-ink capitalize">{o.type === "course" ? "Course enrolment" : "Shop order"}</h3>
                      <Badge status={o.payment_status} />
                    </div>
                    <p className="text-xs text-ink-muted mt-1">
                      {(o.items || []).map((it) => `${it.name}${it.qty > 1 ? ` ×${it.qty}` : ""}`).join(", ") || o.course_title || ""}
                    </p>
                    {o.amount != null && <p className="text-sm font-semibold text-forest mt-1">${Number(o.amount).toFixed(2)}</p>}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
